import authService from './authService';

// ─── Types ──────────────────────────────────────────────────────

export interface ShippingReportFilters {
  date_from: string;
  date_to: string;
  provider_ids?: number[];
  page: number;
  pageSize: number;
}

export interface ShippingReportRow {
  order_id: number;
  company_document: string;
  company_name: string;
  ship_addr_zip: string;
  invoice_number: string | null;
  volume_count: number;
  total_weight: number;
  order_products_total: number;
  freight_provider_id: number;
  freight_provider_name: string;
  freight_cost: number;
}

export interface ShippingReportGroupSummary {
  company_document: string;
  company_name: string;
  provider_id: number;
  provider_name: string;
  total_freight_cost: number;
  total_orders: number;
}

export interface ShippingReportSummary {
  total_orders: number;
  total_freight_cost: number;
  total_weight: number;
  total_products_value: number;
  groups: ShippingReportGroupSummary[];
}

export interface ShippingReportResponse {
  data: ShippingReportRow[];
  summary: ShippingReportSummary;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ShippingProvider {
  provider_id: number;
  provider_name: string;
}

// ─── Pending Delivery Report ─────────────────────────────────────

export interface PendingDeliveryItem {
  order_id: number;
  company_id: number;
  order_num_ecommerce: string | null;
  order_date: string | null;
  shipping_price: number | null;
  order_sell_total: number | null;
  customer_id: number | null;
  customer_name: string | null;
  invoice_id: number | null;
  ship_addr_street: string | null;
  ship_addr_num: string | null;
  ship_addr_compl: string | null;
  ship_addr_district: string | null;
  ship_addr_city: string | null;
  ship_addr_state: string | null;
  ship_addr_zip: string | null;
  shipping_status: string | null;
  shipping_track_url: string | null;
  shipping_update_timestamp: string | null;
  shipping_chosen_by_customer: string | null;
  delivery_time_chosen_by_customer: number | null;
  is_priority: boolean | null;
  request_delivery_time: string | null;
  freight_discount_value: number | null;
  shipping_timestamp: string | null;
  delivery_days: number | null;
  delivery_deadline: string | null; // YYYY-MM-DD (computed business-day deadline)
  is_late: boolean;
  shipping_modality: string | null; // transportadora efetiva (frota própria / integração), não a escolha do checkout
  /**
   * Objetos de rastreio dos Correios, um por volume. Lista não vazia = pedido enviado pela
   * integração direta dos Correios, e é o que habilita a abertura de chamado.
   */
  correios_objects: string[] | null;
}

/** Dados não sensíveis da integração dos Correios (nunca traz credenciais). */
export interface CorreiosIntegration {
  ambiente: string;
  contrato: string;
  cartao_postagem: string;
}

// ─── Delivered Orders Report ─────────────────────────────────────

export interface DeliveredOrdersFilters {
  date_from: string; // YYYY-MM-DD — janela sobre a data de ENTREGA, não a do pedido
  date_to: string;
  page: number;
  pageSize: number;
}

export interface DeliveredOrderItem {
  order_id: number;
  order_num_ecommerce: string | null;
  order_date: string | null;
  delivery_timestamp: string | null;
  customer_id: number | null;
  customer_name: string | null;
  ship_addr_city: string | null;
  ship_addr_state: string | null;
  invoice_id: number | null;
  invoice_number: string | null;
  order_sell_total: number | null;
  shipping_timestamp: string | null;
  shipping_track_url: string | null;
  shipping_modality: string | null; // transportadora efetiva, não a escolha do checkout
}

export interface DeliveredOrdersResponse {
  success: boolean;
  data: DeliveredOrderItem[];
  pagination: {
    page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
  };
}

// ─── Service ────────────────────────────────────────────────────

class ShippingReportService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  private async getBaseUrl(): Promise<string> {
    const { getConfig } = await import('../config');
    return getConfig().api.baseUrl;
  }

  async getReport(filters: ShippingReportFilters): Promise<ShippingReportResponse> {
    const params = new URLSearchParams({
      page: filters.page.toString(),
      limit: filters.pageSize.toString(),
      date_from: filters.date_from,
      date_to: filters.date_to,
    });

    if (filters.provider_ids && filters.provider_ids.length > 0) {
      params.append('provider_ids', filters.provider_ids.join(','));
    }

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/shipping?${params}`);
    if (!response.ok) throw new Error('Erro ao carregar relatório de transporte');

    const json = await response.json();

    const items: ShippingReportRow[] = Array.isArray(json.data) ? json.data : [];
    const pg = json.pagination ?? {};
    const summary: ShippingReportSummary = json.summary ?? {
      total_orders: 0,
      total_freight_cost: 0,
      total_weight: 0,
      total_products_value: 0,
      groups: [],
    };

    return {
      data: items,
      summary,
      pagination: {
        page: pg.page ?? filters.page,
        limit: pg.page_size ?? pg.pageSize ?? filters.pageSize,
        total: pg.total_items ?? pg.total ?? items.length,
        totalPages: pg.total_pages ?? pg.totalPages ?? (Math.ceil((pg.total_items ?? pg.total ?? items.length) / filters.pageSize) || 1),
      },
    };
  }

  async downloadPdf(filters: Omit<ShippingReportFilters, 'page' | 'pageSize'>): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const params = new URLSearchParams({
      date_from: filters.date_from,
      date_to: filters.date_to,
    });
    if (filters.provider_ids && filters.provider_ids.length > 0) {
      params.append('provider_ids', filters.provider_ids.join(','));
    }

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/reports/shipping/pdf?${params}`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || `Erro ao gerar PDF (${response.status})`);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? `relatorio-transporte.pdf`;

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  async downloadExcel(filters: Omit<ShippingReportFilters, 'page' | 'pageSize'>): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const params = new URLSearchParams({
      date_from: filters.date_from,
      date_to: filters.date_to,
    });
    if (filters.provider_ids && filters.provider_ids.length > 0) {
      params.append('provider_ids', filters.provider_ids.join(','));
    }

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/reports/shipping/excel?${params}`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || `Erro ao gerar Excel (${response.status})`);
    }

    // Validate content-type to ensure we got a real Excel file
    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('spreadsheet') && !contentType.includes('octet-stream') && !contentType.includes('excel')) {
      // Backend returned non-binary (likely JSON error)
      const text = await response.text();
      let msg = 'Resposta inválida do servidor ao gerar Excel';
      // Ignorado de propósito: a resposta já é um erro. Se nem JSON ela for,
      // vale a mensagem genérica — trocar um erro por outro não ajuda ninguém.
      try { msg = JSON.parse(text).message || msg; } catch { /* resposta não-JSON */ }
      throw new Error(msg);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? `relatorio-transporte.xlsx`;

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  async getPendingDeliveryReport(): Promise<PendingDeliveryItem[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/reports/shipping/pending-delivery');
    if (!response.ok) throw new Error('Erro ao carregar relatório de pedidos pendentes de entrega');
    const json = await response.json();
    return Array.isArray(json.data) ? json.data : [];
  }

  /** Contrato e cartão de postagem exigidos no Fale Conosco dos Correios. */
  async getCorreiosIntegration(): Promise<CorreiosIntegration> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/reports/shipping/correios-integration');
    if (!response.ok) throw new Error('Erro ao carregar os dados da integração dos Correios');
    const json = await response.json();
    return json.data;
  }

  async getDeliveredOrders(filters: DeliveredOrdersFilters): Promise<DeliveredOrdersResponse> {
    const params = new URLSearchParams({
      date_from: filters.date_from,
      date_to: filters.date_to,
      page: filters.page.toString(),
      limit: filters.pageSize.toString(),
    });
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/shipping/delivered?${params}`);
    if (!response.ok) throw new Error('Erro ao carregar relatório de pedidos entregues');
    return response.json();
  }

  async getProviders(): Promise<ShippingProvider[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/shipping-providers?page=1&page_size=100');
    if (!response.ok) throw new Error('Erro ao carregar transportadoras');
    const json = await response.json();
    const items = json.data?.data ?? json.data ?? [];
    return items.map((p: any) => ({
      provider_id: p.provider_id ?? p.ProviderId,
      provider_name: p.provider_name ?? p.ProviderName,
    }));
  }
}

export const shippingReportService = new ShippingReportService();
export default shippingReportService;
